import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';
import { PrimeNgModule } from '@shared/prime-ng/prime-ng.module';
import { CodeManagementApiService } from '@core/services/code-management-api.service';
import { DateService } from '@core/services/date.service';
import { HcpcsCodeModifierDTO, HcpcsModifierAssociationDTO, IDropdowns } from '@core/models';

@Component({
  selector: 'app-modifiers',
  templateUrl: './modifiers.component.html',
  styleUrls: ['./modifiers.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PrimeNgModule
  ],
  providers: [MessageService, ConfirmationService]
})
export class ModifiersComponent implements OnInit, OnChanges {
  @Input() mode: 'add' | 'edit' | 'view' = 'add';
  @Input() initialModifiers?: HcpcsModifierAssociationDTO[];
  @Input() planEffectiveStartDate?: Date | null;
  @Input() planEffectiveEndDate?: Date | null;
  @Input() cmsEffectiveStartDate?: Date | null;
  @Input() cmsEffectiveEndDate?: Date | null;
  @Input() hcpcsCode?: string;
  @Input() tenantId?: string;
  @Input() isLoading: boolean = false;
  @Output() initialLoadComplete = new EventEmitter<void>();

  modifiersFormGroup!: FormGroup;
  availableModifiers: IDropdowns[] = [];
  yesNoOptions: IDropdowns[] = [
    { label: 'Select', value: '' },
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' }
  ];

  isVisible = true;
  isValidatingModifier = false;

  constructor(
    private fb: FormBuilder,
    private codeManagementApiService: CodeManagementApiService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public cdr: ChangeDetectorRef,
    private dateService: DateService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadAvailableModifiers();
    
    if (this.initialModifiers && this.initialModifiers.length > 0) {
      this.loadInitialModifiers();
    }
    
    this.initialLoadComplete.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialModifiers'] && !changes['initialModifiers'].firstChange && this.mode === 'view') {
      const newModifiers = changes['initialModifiers'].currentValue;
      
      // Clear existing modifiers
      this.modifiersList.clear();
      
      // Load new modifiers
      if (newModifiers && newModifiers.length > 0) {
        this.loadInitialModifiers();
      }
    }

    if (this.mode !== 'view' && (changes['planEffectiveStartDate'] || changes['planEffectiveEndDate'] || 
        changes['cmsEffectiveStartDate'] || changes['cmsEffectiveEndDate'])) {
      
      if (!this.modifiersFormGroup) {
        return;
      }
      
      const arePlanDatesValid = !this.planEffectiveStartDate || !this.planEffectiveEndDate || 
                                new Date(this.planEffectiveStartDate) <= new Date(this.planEffectiveEndDate);
      
      const newmodifierEffectiveStartDate = arePlanDatesValid 
        ? (this.planEffectiveStartDate ?? this.cmsEffectiveStartDate ?? null)
        : (this.cmsEffectiveStartDate ?? null);
      const newmodifierEffectiveEndDate = arePlanDatesValid 
        ? (this.planEffectiveEndDate ?? this.cmsEffectiveEndDate ?? null)
        : (this.cmsEffectiveEndDate ?? null);
      
      this.modifiersList.controls.forEach((control) => {
        const formGroup = control as FormGroup;
        
        if (formGroup.get('isEditing')?.value) {
          if (formGroup.get('isStartDateLocked')?.value) {
            formGroup.patchValue({ modifierEffectiveStartDate: newmodifierEffectiveStartDate }, { emitEvent: false });
          }
          if (formGroup.get('isEndDateLocked')?.value) {
            formGroup.patchValue({ modifierEffectiveEndDate: newmodifierEffectiveEndDate }, { emitEvent: false });
          }
        }
      });
    }
  }

  private initializeForm(): void {
    this.modifiersFormGroup = this.fb.group({
      modifiers: this.fb.array([])
    });
  }

  get modifiersList(): FormArray {
    return this.modifiersFormGroup.get('modifiers') as FormArray;
  }

  private loadAvailableModifiers(): void {
    this.codeManagementApiService.getAllModifiers().subscribe({
      next: (modifiers: HcpcsCodeModifierDTO[]) => {
        this.availableModifiers = modifiers.map(m => ({
          label: m.modifierName,
          value: m.modifierId ?? null
        }));
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load modifiers'
        });
      }
    });
  }

  private loadInitialModifiers(): void {
    if (!this.initialModifiers) return;

    this.initialModifiers.forEach(modifier => {
      const formGroup = this.fb.group({
        hcpcsByModifierId: [modifier.hcpcsByModifierId],
        modifierId: [modifier.modifierId],
        modifier: [modifier.modifier, Validators.required],
        delegated: [modifier.delegated ? 'Yes' : 'No', Validators.required],
        paRequired: [modifier.paRequired ? 'Yes' : 'No', Validators.required],
        paAllowed: [modifier.paAllowed ? 'Yes' : 'No', Validators.required],
        modifierEffectiveStartDate: [modifier.modifierEffectiveStartDate ? new Date(modifier.modifierEffectiveStartDate) : null, Validators.required],
        modifierEffectiveEndDate: [{ value: modifier.modifierEffectiveEndDate ? new Date(modifier.modifierEffectiveEndDate) : null, disabled: true }],
        status: [modifier.status],
        isEditing: [false],
        isStartDateLocked: [true],
        isEndDateLocked: [true]
      });
      
      this.setupDateValidation(formGroup);
      this.modifiersList.push(formGroup);
    });
  }

  private createModifierFormGroup(isEditing: boolean): FormGroup {
    const startDate = this.planEffectiveStartDate || this.cmsEffectiveStartDate;
    const endDate = this.planEffectiveEndDate || this.cmsEffectiveEndDate || null;

    const formGroup = this.fb.group({
      hcpcsByModifierId: [null],
      modifierId: [''],
      modifier: ['', Validators.required],
      delegated: ['', Validators.required],
      paRequired: ['', Validators.required],
      paAllowed: ['', Validators.required],
      modifierEffectiveStartDate: [startDate],
      modifierEffectiveEndDate: [{ value: endDate, disabled: true }],
      status: [null],
      isEditing: [isEditing],
      isStartDateLocked: [true],
      isEndDateLocked: [true]
    });

    this.setupDateValidation(formGroup);
    return formGroup;
  }

  private setupDateValidation(formGroup: FormGroup): void {
    formGroup.get('modifierEffectiveStartDate')?.valueChanges.subscribe(modifierEffectiveStartDate => {
      const modifierEffectiveEndDateControl = formGroup.get('modifierEffectiveEndDate');
      const modifierEffectiveEndDateValue = modifierEffectiveEndDateControl?.value;
      const isEndDateLocked = formGroup.get('isEndDateLocked')?.value;
      
      if (modifierEffectiveStartDate && modifierEffectiveEndDateValue && 
          new Date(modifierEffectiveStartDate) > new Date(modifierEffectiveEndDateValue)) {
        modifierEffectiveEndDateControl?.patchValue(null, { emitEvent: false });
      }
      
      if (modifierEffectiveStartDate && !isEndDateLocked) {
        modifierEffectiveEndDateControl?.enable({ emitEvent: false });
      } else if (!modifierEffectiveStartDate && !isEndDateLocked) {
        modifierEffectiveEndDateControl?.disable({ emitEvent: false });
      }
    });
  }

  getModifierGroup(index: number): FormGroup {
    return this.modifiersList.at(index) as FormGroup;
  }

  isRowEditable(index: number): boolean {
    return this.getModifierGroup(index).get('isEditing')?.value === true;
  }

  isExistingModifier(index: number): boolean {
    const hcpcsByModifierId = this.getModifierGroup(index).get('hcpcsByModifierId')?.value;
    return hcpcsByModifierId !== null && hcpcsByModifierId !== undefined;
  }

  isStartDateLocked(index: number): boolean {
    return this.getModifierGroup(index).get('isStartDateLocked')?.value === true;
  }

  isEndDateLocked(index: number): boolean {
    return this.getModifierGroup(index).get('isEndDateLocked')?.value === true;
  }

  toggleStartDateLock(index: number): void {
    const formGroup = this.getModifierGroup(index);
    const currentValue = formGroup.get('isStartDateLocked')?.value;
    formGroup.patchValue({ isStartDateLocked: !currentValue });

    if (!currentValue) {
      const defaultDate = this.planEffectiveStartDate || this.cmsEffectiveStartDate;
      formGroup.patchValue({ modifierEffectiveStartDate: defaultDate });
    } else {
      this.messageService.add({
        severity: 'info',
        summary: 'Effective Dates Unlocked',
        detail: 'You can proceed with editing'
      });
    }
  }

  toggleEndDateLock(index: number): void {
    const formGroup = this.getModifierGroup(index);
    const currentValue = formGroup.get('isEndDateLocked')?.value;
    formGroup.patchValue({ isEndDateLocked: !currentValue });

    if (!currentValue) {
      const defaultDate = this.planEffectiveEndDate || this.cmsEffectiveEndDate;
      formGroup.patchValue({ modifierEffectiveEndDate: defaultDate });
    } else {
      const modifierEffectiveStartDate = formGroup.get('modifierEffectiveStartDate')?.value;
      const modifierEffectiveEndDateControl = formGroup.get('modifierEffectiveEndDate');
      
      if (modifierEffectiveStartDate) {
        modifierEffectiveEndDateControl?.enable();
      } else {
        modifierEffectiveEndDateControl?.disable();
      }
      
      this.messageService.add({
        severity: 'info',
        summary: 'Effective Dates Unlocked',
        detail: 'You can proceed with editing'
      });
    }
  }

  onModifierSelected(index: number): void {
    const formGroup = this.getModifierGroup(index);
    const selectedModifierId = formGroup.get('modifierId')?.value;
    const selectedModifier = this.availableModifiers.find(m => m.value === selectedModifierId);

    if (selectedModifier) {
      const isNoModifier = selectedModifierId === null;
      
      if (isNoModifier) {
        const hasOtherModifiers = this.modifiersList.controls.some((control, i) => 
          i !== index && control.get('modifierId')?.value && control.get('modifierId')?.value !== ''
        );
        
        if (hasOtherModifiers) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Invalid Selection',
            detail: '"No Modifier" cannot be added when other modifiers already exist. Please remove existing modifiers first.'
          });
          formGroup.patchValue({ modifierId: '', modifier: '' });
          return;
        }
      } else {
        const hasNoModifier = this.modifiersList.controls.some((control, i) => 
          i !== index && control.get('modifierId')?.value === null
        );
        
        if (hasNoModifier) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Invalid Selection',
            detail: 'Cannot add modifiers when "No Modifier" is already selected. Please remove "No Modifier" first.'
          });
          formGroup.patchValue({ modifierId: '', modifier: '' });
          return;
        }
      }

      formGroup.patchValue({ modifier: selectedModifier.label });

      const isDuplicate = this.modifiersList.controls.some((control, i) => 
        i !== index && control.get('modifierId')?.value === selectedModifierId
      );

      if (isDuplicate) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Duplicate Modifier',
          detail: 'This modifier has already been added'
        });
        formGroup.patchValue({ modifierId: '', modifier: '' });
      }
    }
  }

  addModifier(): void {
    const formGroup = this.createModifierFormGroup(true);
    this.modifiersList.push(formGroup);
  }

  editModifier(index: number): void {
    this.getModifierGroup(index).patchValue({ isEditing: true });
  }

  saveModifier(index: number): void {
    const formGroup = this.getModifierGroup(index);
    
    if (formGroup.invalid) {
      formGroup.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Required Field Missing',
        detail: 'To proceed complete the missing info first'
      });
      return;
    }

    const isExisting = this.isExistingModifier(index);
    formGroup.patchValue({ isEditing: false });
    
    this.messageService.add({
      severity: 'success',
      summary: isExisting ? 'Modifier(s) Saved' : 'Modifier(s) Added',
      detail: isExisting ? 'Modifier(s) was successfully saved' : 'Modifier(s) was successfully added'
    });
  }

  cancelModifier(index: number): void {
    const formGroup = this.getModifierGroup(index);
    const hcpcsByModifierId = formGroup.get('hcpcsByModifierId')?.value;

    if (!hcpcsByModifierId) {
      this.modifiersList.removeAt(index);
    } else {
      formGroup.patchValue({ isEditing: false });
      if (this.initialModifiers && this.initialModifiers[index]) {
        const original = this.initialModifiers[index];
        formGroup.patchValue({
          modifierId: original.modifierId,
          modifier: original.modifier,
          delegated: original.delegated ? 'Yes' : 'No',
          paRequired: original.paRequired ? 'Yes' : 'No',
          paAllowed: original.paAllowed ? 'Yes' : 'No',
          modifierEffectiveStartDate: original.modifierEffectiveStartDate ? new Date(original.modifierEffectiveStartDate) : null,
          modifierEffectiveEndDate: original.modifierEffectiveEndDate ? new Date(original.modifierEffectiveEndDate) : null
        });
      }
    }
  }

  deleteModifier(index: number): void {
    const formGroup = this.getModifierGroup(index);
    const modifierCode = formGroup.get('modifier')?.value;
    const hcpcsByModifierId = formGroup.get('hcpcsByModifierId')?.value;
    
    this.confirmationService.confirm({
      header: 'Delete Modifier',
      message: `<strong>Are you sure you want to delete modifier ${modifierCode}?</strong><br>This action can't be undone.`,
      icon: '',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      blockScroll: false,
      accept: () => {
        if (!hcpcsByModifierId) {
          this.modifiersList.removeAt(index);
          this.showModifierDeletedSuccess();
          return;
        }
        
        this.validateModifierAndDelete(hcpcsByModifierId, index);
      }
    });
  }

  private validateModifierAndDelete(hcpcsByModifierId: string, index: number): void {
    this.isValidatingModifier = true;
    
    this.codeManagementApiService.isModifierInUseByAuthorization(
      hcpcsByModifierId, 
      this.tenantId!
    ).subscribe({
      next: (isModifierInUse) => {
        this.isValidatingModifier = false;
        
        if (isModifierInUse) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Unable to Delete',
            detail: 'This modifier is used by an authorization(s) and can\'t be deleted.',
            life: 5000
          });
        } else {
          this.modifiersList.removeAt(index);
          this.showModifierDeletedSuccess();
        }
      },
      error: () => {
        this.isValidatingModifier = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Something went wrong. Please try again or contact Portal support.'
        });
      }
    });
  }

  private showModifierDeletedSuccess(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Modifier Deleted',
      detail: 'Modifier was successfully deleted.'
    });
  }

  getModifiersData(): any[] {
    return this.modifiersList.controls.map(control => {
      const formGroup = control as FormGroup;
      const value = formGroup.getRawValue();
      return {
        hcpcsByModifierId: value.hcpcsByModifierId,
        modifierId: value.modifierId === '' ? null : (value.modifierId || null),
        delegated: value.delegated === 'Yes',
        paRequired: value.paRequired === 'Yes',
        paAllowed: value.paAllowed === 'Yes',
        modifierEffectiveStartDate: this.dateService.transformPOSTDate(value.modifierEffectiveStartDate),
        modifierEffectiveEndDate: this.dateService.transformPOSTDate(value.modifierEffectiveEndDate)
      };
    });
  }
}
